import type { HubSpotProperty } from '@/store/features/crm.feature';
import type { SalesforceTriggerCondition } from '@/types/crm-enrichment-playbook';
import {
  defaultTriggerValue,
  resolveSalesforceFieldType,
  triggerOperatorsFor,
  VALUELESS_TRIGGER_OPERATORS,
  type TriggerOperator,
} from '@/utils/playbook-trigger.utils';

export const CRM_CHANGE_OPERATORS = new Set([
  'changed',
  'changed_to',
  'changed_from',
]);
export const CRM_CHANGE_VALUELESS = new Set([
  ...VALUELESS_TRIGGER_OPERATORS,
  'changed',
]);

function record(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** Inspect saved JSON without rewriting unsupported or incomplete drafts. */
export function hasCrmChangeConditions(value: unknown): boolean {
  const pending: unknown[] = [value];
  const seen = new Set<unknown>();
  while (pending.length) {
    const next = pending.pop();
    if ((!record(next) && !Array.isArray(next)) || seen.has(next)) continue;
    seen.add(next);
    if (seen.size > 1000) return false;
    if (
      record(next) &&
      typeof next.operator === 'string' &&
      CRM_CHANGE_OPERATORS.has(next.operator)
    )
      return true;
    pending.push(...(Array.isArray(next) ? next : Object.values(next)));
  }
  return false;
}

export function crmChangeConditionCount(value: unknown): number {
  if (!record(value) || !Array.isArray(value.conditions)) return 0;
  return value.conditions.filter(
    (condition) =>
      record(condition) &&
      typeof condition.operator === 'string' &&
      CRM_CHANGE_OPERATORS.has(condition.operator)
  ).length;
}

/** Change observations support scalar own fields, never a parent relationship. */
export function changeOperatorsForCondition(
  property: HubSpotProperty | undefined,
  condition: SalesforceTriggerCondition,
  changes: number
): TriggerOperator[] {
  const type = condition.fieldType ?? resolveSalesforceFieldType(property);
  const ordinary = triggerOperatorsFor(property, type);
  if (!property || property.hidden || property.name.includes('.'))
    return ordinary;
  const raw = (property.field_type || property.type || '').toLowerCase();
  if (
    type === 'multipicklist' ||
    !new Set([
      'text',
      'string',
      'textarea',
      'email',
      'phone',
      'phone_number',
      'url',
      'encryptedstring',
      'picklist',
      'combobox',
      'enumeration',
      'select',
      'bool',
      'boolean',
      'booleancheckbox',
      'number',
      'numeric',
      'currency',
      'int',
      'integer',
      'long',
      'double',
      'percent',
      'date',
      'datetime',
    ]).has(raw)
  )
    return ordinary;
  const disabled =
    changes >= 3 && !CRM_CHANGE_OPERATORS.has(condition.operator);
  return [
    ...ordinary,
    { value: 'changed', label: 'Changes', disabled },
    { value: 'changed_to', label: 'Changes to', disabled },
    { value: 'changed_from', label: 'Changes from', disabled },
  ];
}

const ATTIO_MATCH = ['equals', 'not_equals', 'in', 'not_in'];
const ATTIO_ORDER = ['greater', 'gte', 'less', 'lte'];
const ATTIO_PRESENCE = ['exists', 'not_exists'];

/**
 * The operators an Attio search takes on each Attio attribute type, in the
 * backend's names: `attio_filters.ATTIO_OPERATORS`. A type not here (names,
 * references, phones, locations) can't be searched. The builder's save check
 * (`graph-ops.ts`) and the condition editor both read this one table, and
 * `fixtures/crm_change_conditions.json` pins it to the backend's.
 */
export const ATTIO_OPERATORS: Readonly<Record<string, readonly string[]>> = {
  text: [...ATTIO_MATCH, 'contains', ...ATTIO_PRESENCE],
  // Attio refuses "is known" on an email address.
  'email-address': [...ATTIO_MATCH, 'contains'],
  domain: [...ATTIO_MATCH, 'contains', ...ATTIO_PRESENCE],
  select: [...ATTIO_MATCH, ...ATTIO_PRESENCE],
  status: [...ATTIO_MATCH, ...ATTIO_PRESENCE],
  number: [...ATTIO_MATCH, ...ATTIO_ORDER, ...ATTIO_PRESENCE],
  currency: [...ATTIO_MATCH, ...ATTIO_ORDER, ...ATTIO_PRESENCE],
  date: [...ATTIO_MATCH, ...ATTIO_ORDER, ...ATTIO_PRESENCE],
  // An instant to the nanosecond: Attio's "is" would match nothing.
  timestamp: [...ATTIO_ORDER, ...ATTIO_PRESENCE],
  checkbox: ['equals', 'not_equals'],
};

/** Other spellings the backend reads as the operators above, the editor's
 *  among them (`greater_than` is `greater`). */
export const ATTIO_OPERATOR_ALIASES: Readonly<Record<string, string>> = {
  eq: 'equals',
  ne: 'not_equals',
  neq: 'not_equals',
  gt: 'greater',
  lt: 'less',
  greater_than: 'greater',
  less_than: 'less',
  greater_than_or_equal: 'gte',
  less_than_or_equal: 'lte',
  empty: 'not_exists',
  not_empty: 'exists',
};

/** The field types an Attio change can watch, as the kind each compares as
 *  (`crm_change_trigger._ATTIO_KINDS`). An email address or a domain can't. */
export const ATTIO_CHANGE_KINDS: Readonly<Record<string, string>> = {
  text: 'text',
  string: 'text',
  picklist: 'text',
  select: 'text',
  status: 'text',
  boolean: 'boolean',
  checkbox: 'boolean',
  number: 'number',
  currency: 'number',
  rating: 'number',
  date: 'date',
  datetime: 'date',
  timestamp: 'date',
};

/** A table entry, never an inherited key such as `constructor`. */
function ownValue<T>(table: Readonly<Record<string, T>>, key: string) {
  return Object.prototype.hasOwnProperty.call(table, key)
    ? table[key]
    : undefined;
}

/** The operators Attio searches on an Attio type, or undefined. */
export function attioSearchOperators(
  type: string
): readonly string[] | undefined {
  return ownValue(ATTIO_OPERATORS, type);
}

/** An operator in the backend's spelling. */
export function attioOperatorName(operator: string): string {
  const lower = operator.toLowerCase();
  return ownValue(ATTIO_OPERATOR_ALIASES, lower) ?? lower;
}

/** The kind an Attio change on this type compares as, or undefined when a
 *  change can't watch it. */
export function attioChangeKind(type: string): string | undefined {
  return ownValue(ATTIO_CHANGE_KINDS, type);
}

/** How the shared condition builder reads some Attio types: a status picks
 *  from its statuses as a select does, a timestamp takes a date and time, a
 *  checkbox is a yes or no. The trigger panel hands the builder its rows in
 *  these types; every other Attio type reads as the builder already reads it. */
export const ATTIO_BUILDER_TYPES: Readonly<Record<string, string>> = {
  status: 'select',
  timestamp: 'datetime',
  checkbox: 'boolean',
};

/** A row's type read back as Attio's: the builder types above and the family
 *  names the attributes route sends as `type`. A status read as a select
 *  stays a select, which takes the same operators. */
const ATTIO_TYPE_ALIASES: ReadonlyMap<string, string> = new Map([
  ['enumeration', 'select'],
  ['datetime', 'timestamp'],
  ['boolean', 'checkbox'],
  ['bool', 'checkbox'],
]);

/** Each Attio type as a Salesforce or HubSpot type the editor's operator
 *  lists already read (`resolveSalesforceFieldType`, the change list). This
 *  picks the operator list only; the value editor reads the row's own type. */
const ATTIO_EDITOR_TYPES: ReadonlyMap<string, string> = new Map([
  ['text', 'text'],
  ['email-address', 'email'],
  ['domain', 'text'],
  ['select', 'select'],
  ['status', 'select'],
  ['number', 'number'],
  ['currency', 'currency'],
  ['rating', 'number'],
  ['date', 'date'],
  ['timestamp', 'datetime'],
  ['checkbox', 'boolean'],
]);

function attioType(property: HubSpotProperty): string {
  const raw = (property.field_type || property.type || '').toLowerCase();
  return ATTIO_TYPE_ALIASES.get(raw) ?? raw;
}

/**
 * An Attio condition's operators. `operatorsFor` (the editor's own list, or
 * the trigger's with its changes) is asked with the attribute in the type it
 * reads, then only what Attio searches on the attribute's Attio type is kept,
 * and the three changes only where Attio can watch that type. A type Attio
 * can't search, or no attribute read, offers nothing, so an Attio condition
 * never offers an operator the save would refuse.
 */
export function attioConditionOperators(
  property: HubSpotProperty | undefined,
  condition: SalesforceTriggerCondition,
  operatorsFor: (
    property: HubSpotProperty,
    condition: SalesforceTriggerCondition
  ) => TriggerOperator[]
): TriggerOperator[] {
  if (!property) return [];
  const type = attioType(property);
  const editorType = ATTIO_EDITOR_TYPES.get(type);
  const search = attioSearchOperators(type);
  if (!editorType) return [];
  const editor = { ...property, field_type: editorType, type: editorType };
  return operatorsFor(editor, {
    ...condition,
    fieldType: resolveSalesforceFieldType(editor),
  }).filter((operator) =>
    CRM_CHANGE_OPERATORS.has(operator.value)
      ? attioChangeKind(type) !== undefined
      : !!search?.includes(attioOperatorName(operator.value))
  );
}

export function attioConditionFieldDefaults(
  property: HubSpotProperty,
  condition: SalesforceTriggerCondition,
  operators: TriggerOperator[],
  valuelessOperators: ReadonlySet<string>
): Partial<SalesforceTriggerCondition> {
  const fieldType = resolveSalesforceFieldType(property);
  const operator = operators.some(
    (candidate) => candidate.value === condition.operator && !candidate.disabled
  )
    ? condition.operator
    : (operators.find((candidate) => !candidate.disabled)?.value ?? '');
  return {
    field: property.name,
    fieldType,
    operator,
    value: valuelessOperators.has(operator)
      ? undefined
      : defaultTriggerValue(fieldType, operator),
  };
}

export function crmChangeConditionsIssue(value: unknown): string | null {
  if (!hasCrmChangeConditions(value)) return null;
  if (
    !record(value) ||
    value.operator !== 'AND' ||
    !Array.isArray(value.conditions) ||
    value.conditions.some(
      (condition) => !record(condition) || 'conditions' in condition
    )
  )
    return 'Changes work in an AND group with no nested groups.';
  if (crmChangeConditionCount(value) > 3) return 'Up to 3 change conditions.';
  for (const condition of value.conditions) {
    if (!record(condition) || typeof condition.operator !== 'string')
      return 'Complete every condition.';
    if (!CRM_CHANGE_OPERATORS.has(condition.operator)) continue;
    if (
      typeof condition.field !== 'string' ||
      !condition.field ||
      condition.field.includes('.')
    )
      return "Use the record's own fields for changes.";
    if (
      (condition.fieldType !== undefined &&
        typeof condition.fieldType !== 'string') ||
      ![
        'text',
        'string',
        'picklist',
        'boolean',
        'number',
        'date',
        'datetime',
      ].includes(condition.fieldType ?? 'text')
    )
      return 'Changes need a text, picklist, boolean, number or date field.';
    if (
      condition.operator !== 'changed' &&
      !Object.prototype.hasOwnProperty.call(condition, 'value')
    )
      return 'Pick the value the field changes to or from.';
  }
  return null;
}
